import { message } from "antd";

import { useAuthStore } from "@/stores/authStore";
import { PromotionRuleInterface } from "@/types/authService";
import { MdmData, PledgeItemInterface, SelectedPledgeItem } from "@/types/pledgeService";

export const ApiStatusCheck = (status: number, apiMessage: string) => {
  switch (status) {
    case 401:
      console.warn("Unauthorized");
      useAuthStore.getState().logout();
      break;

    case 403:
      console.warn("Forbidden:", apiMessage || "Access denied");
      message.error("You don’t have permission to access this resource.");
      break;

    case 404:
      console.warn("Not Found:", apiMessage);
      message.error("Requested resource not found.");
      break;

    case 500:
      console.error("Server Error:", apiMessage);
      message.error(apiMessage || "Server error. Please try again later.");
      break;

    default:
      console.error("Error:", apiMessage || "Something went wrong.");
      message.error(apiMessage || "Something went wrong.");
      break;
  }
};

export const validatePassword = (password: string, minLength: number = 8): boolean => {
  const regex = new RegExp(
    `^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-={}\\[\\]|:;"'<>,.?/]).{${minLength},}$`,
  );

  return regex.test(password);
};

export const calculateFinalPledgeAmountForRenewCheckout = (
  rules: PromotionRuleInterface[],
  pledgeAmount: number,
  ltv: number,
  pledgeDate: string,
  normalInterestRate: number,
): any => {
  const matchedRule = rules.find((rule) => {
    return (
      pledgeAmount >= rule.min_amount &&
      pledgeAmount <= rule.max_amount &&
      ltv >= rule.min_LTV &&
      ltv <= rule.max_LTV
    );
  });

  if (!matchedRule) {
    throw new Error("Promotion code not applicable");
  }

  const startDate = new Date(pledgeDate);
  const currentDate = new Date();

  const totalMonths =
    (currentDate.getFullYear() - startDate.getFullYear()) * 12 +
    (currentDate.getMonth() - startDate.getMonth()) +
    1;

  let totalInterest = 0;

  for (let month = 1; month <= totalMonths; month++) {
    if (month >= matchedRule.min_entitlement_month && month <= matchedRule.max_entitlement_month) {
      totalInterest += (pledgeAmount * matchedRule.interest_rate) / 100;
    } else {
      totalInterest += (pledgeAmount * normalInterestRate) / 100;
    }
  }

  const finalPledgeAmount = pledgeAmount + totalInterest;

  return {
    appliedRuleId: matchedRule.id,
    totalMonths,
    totalInterest,
    finalPledgeAmount,
  };
};

//  Input
// const rules = [
//   {
//     id: 26,
//     transaction_type: "ALL",
//     interest_rate: 5,
//     min_amount: 100,
//     max_amount: 1000,
//     min_duration: 1,
//     max_duration: 12,
//     min_entitlement_month: 6,
//     max_entitlement_month: 12,
//     min_LTV: 50,
//     max_LTV: 80,
//     is_active: true,
//     is_deleted: false,
//   },
// ];

// const input = {
//   pledgeAmount: 500,
//   ltv: 60,
//   pledgeDate: "2025-11-10T00:00:00.000Z",
//   normalInterestRate: 8,
//   rules
// };

// // Output

// {
//   appliedRuleId: 26,
//   totalMonths: 8,
//   totalInterest: 275,
//   finalPledgeAmount: 775
// }

export const generateRandomDigitRef = (length: number = 25): string => {
  const timestamp = Date.now().toString();

  const randomArray = new Uint32Array(5);
  crypto.getRandomValues(randomArray);

  const randomPart = Array.from(randomArray).join("");

  const combined = `${timestamp}${randomPart}`;

  return combined.slice(0, length);
};

export const validatePledgesMinimumDownloanAmountP034 = (
  pledgesDetails: SelectedPledgeItem[],
  minimumRemainingAmount: number,
): string[] => {
  return pledgesDetails
    .filter((record) => {
      const remainingPledgeAmount = record.offered_pledge_amount - (record.downloanAmount ?? 0);
      return remainingPledgeAmount < minimumRemainingAmount;
    })
    .map((pledge) => pledge.ticket_alpha_code);
};

export const validateMinimumDownloanAmountAfterRenewalP035 = (
  pledgesDetails: SelectedPledgeItem[],
  minimumDownloanAmount: number,
): string[] => {
  return pledgesDetails
    .filter((pledge) => (pledge.downloanAmount ?? 0) < minimumDownloanAmount)
    .map((pledge) => pledge.ticket_alpha_code);
};

export const getPledgesBelowMinimumAmount = (
  pledgesDetails: PledgeItemInterface[],
  othersMdmDetails: MdmData[],
): string[] => {
  const findMdm = othersMdmDetails?.find((item) => item?.reference_id === "P033");
  const minimumPledgeAmount = findMdm?.value ?? 0;
  return pledgesDetails
    .filter((pledge) => pledge.offered_pledge_amount <= minimumPledgeAmount)
    .map((pledge) => pledge.ticket_alpha_code);
};
